/** Read-only preview of a user, in the shared right-side drawer. */

import { SheetTitle } from '@/components/ui/sheet';
import { PreviewSheet } from '../PreviewSheet';
import { ChevronIcon } from '../icons/ChevronIcon';
import { ExternalLinkIcon } from '../icons';
import { MobileIconButton } from '../layouts/MobileButtons';
import { FooterEditLink } from '../common/FooterEditLink';
import { UserAccountSection } from './UserAccountSection';
import { UserStatusDropdown } from './UserStatusDropdown';
import { canEditUser } from './canEditUser';
import { useTranslation } from '@hooks/useTranslation';
import type { User } from '@api/users';
import type { CurrentUser } from '../../lib/capabilities';

interface UserPreviewSheetProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Without edit rights on the user, the status control renders disabled, not absent. */
  currentUser?: CurrentUser;
  onStatusChange: (user: User, next: User['status']) => void;
}

function SheetHeader({
  user,
  onClose,
  currentUser,
  onStatusChange,
}: {
  user: User;
  onClose: () => void;
  currentUser?: CurrentUser;
  onStatusChange: (user: User, next: User['status']) => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex min-w-0 items-center gap-3">
        {/* Below md the drawer is full-bleed, so the backdrop is unreachable. */}
        <MobileIconButton
          onClick={onClose}
          aria-label={t('common.close')}
          className="size-14 md:hidden"
        >
          <ChevronIcon direction="left" />
        </MobileIconButton>
        {/* Only the icon is the link; the title stays static off-black. */}
        <a
          href={`/users/${user.id}/details`}
          aria-label={t('preview_drawer.title')}
          className="text-grey transition-colors hover:text-off-black"
        >
          <ExternalLinkIcon />
        </a>
        {/* SheetTitle, not a bare span: it is what names the dialog. */}
        <SheetTitle asChild>
          <span className="truncate font-inter text-lg font-semibold text-off-black">
            {t('preview_drawer.title')}
          </span>
        </SheetTitle>
      </div>

      <UserStatusDropdown
        user={user}
        canWrite={canEditUser(currentUser, user)}
        onStatusChange={onStatusChange}
      />
    </>
  );
}

export function UserPreviewSheet({
  user,
  open,
  onOpenChange,
  currentUser,
  onStatusChange,
}: UserPreviewSheetProps) {
  const { t } = useTranslation();

  return (
    <PreviewSheet
      // The selected row can leave the list; close rather than render empty.
      open={open && user !== null}
      onOpenChange={onOpenChange}
      header={
        user ? (
          <SheetHeader
            user={user}
            onClose={() => onOpenChange(false)}
            currentUser={currentUser}
            onStatusChange={onStatusChange}
          />
        ) : undefined
      }
      footerClassName="justify-end"
      footer={
        user && canEditUser(currentUser, user) ? (
          <FooterEditLink href={`/users/${user.id}/edit`} label={t('actions.edit')} />
        ) : undefined
      }
    >
      {user && <UserAccountSection user={user} />}
    </PreviewSheet>
  );
}
